import React, { useState } from 'react';
import { useStore } from '@/store/useStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Download, 
  FileSpreadsheet, 
  FileText, 
  Building2, 
  Droplets, 
  BarChart3,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const ExportPage = () => {
  const { operators, wells, counties, basinData, priceDeck } = useStore();
  const [exporting, setExporting] = useState(null);
  const [exported, setExported] = useState([]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num?.toFixed(2) || '0';
  };

  const downloadCSV = (data, filename, headers) => {
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => {
        const value = row[h.toLowerCase().replace(/ /g, '_')] ?? row[h] ?? '';
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportOperatorsCSV = () => {
    setExporting('operators-csv');
    setTimeout(() => {
      const headers = ['Name', 'Total_Wells', 'Active_Wells', 'Total_Oil_Production', 'Total_Gas_Production', 'Momentum_Score', 'Growth_Rate'];
      const data = operators.map(op => ({
        name: op.name,
        total_wells: op.total_wells,
        active_wells: op.active_wells,
        total_oil_production: op.total_oil_production,
        total_gas_production: op.total_gas_production,
        momentum_score: op.momentum_score,
        growth_rate: op.growth_rate
      }));
      downloadCSV(data, 'forecast_wells_operators', headers);
      setExporting(null);
      setExported(prev => [...prev, 'operators-csv']);
    }, 500);
  };

  const exportCountiesCSV = () => {
    setExporting('counties-csv');
    setTimeout(() => {
      const headers = ['Name', 'Total_Oil', 'Total_Gas', 'Well_Count', 'Active_Wells', 'Oil_Rank', 'Gas_Rank', 'Latitude', 'Longitude'];
      const data = counties.map(c => ({
        name: c.name,
        total_oil: c.total_oil,
        total_gas: c.total_gas,
        well_count: c.well_count,
        active_wells: c.active_wells,
        oil_rank: c.oil_rank || '',
        gas_rank: c.gas_rank || '',
        latitude: c.lat,
        longitude: c.lng
      }));
      downloadCSV(data, 'forecast_wells_counties', headers);
      setExporting(null);
      setExported(prev => [...prev, 'counties-csv']);
    }, 500);
  };

  const exportWellsCSV = () => {
    setExporting('wells-csv');
    setTimeout(() => {
      const headers = ['API_Number', 'Operator_Name', 'County', 'Field', 'Status', 'First_Production_Date'];
      const data = wells.slice(0, 1000).map(w => ({
        api_number: w.api_number,
        operator_name: w.operator_name,
        county: w.county,
        field: w.field,
        status: w.status,
        first_production_date: w.first_production_date
      }));
      downloadCSV(data, 'forecast_wells_wells', headers);
      setExporting(null);
      setExported(prev => [...prev, 'wells-csv']);
    }, 500);
  };

  const exportSummaryPDF = () => {
    setExporting('summary-pdf');
    setTimeout(() => {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFillColor(30, 30, 30);
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('Forecast Wells', 14, 20);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Texas Permian Basin Production Report', 14, 30);
      
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString()} | Price Deck: $${priceDeck}/bbl`, 14, 38);

      // Basin Overview Section
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Basin Overview', 14, 55);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      const overviewData = [
        ['Metric', 'Value'],
        ['Total Oil Production (24mo)', `${formatNumber(basinData?.total_oil_production || 0)} BBL`],
        ['Total Gas Production (24mo)', `${formatNumber(basinData?.total_gas_production || 0)} MCF`],
        ['Active Wells', `${(basinData?.active_wells || 0).toLocaleString()}`],
        ['Total Wells', `${(basinData?.total_wells || 0).toLocaleString()}`],
        ['Active Operators', `${basinData?.total_operators || 0}`],
      ];

      doc.autoTable({
        startY: 60,
        head: [overviewData[0]],
        body: overviewData.slice(1),
        theme: 'striped',
        headStyles: { fillColor: [45, 212, 191], textColor: [0, 0, 0] },
        margin: { left: 14, right: 14 },
      });

      // Top Operators Section
      const topOpsY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Top Operators by Production', 14, topOpsY);

      const operatorData = operators.slice(0, 10).map(op => [
        op.name,
        op.total_wells.toLocaleString(),
        op.active_wells.toLocaleString(),
        formatNumber(op.total_oil_production),
        `${op.momentum_score}`,
        `${op.growth_rate > 0 ? '+' : ''}${op.growth_rate}%`
      ]);

      doc.autoTable({
        startY: topOpsY + 5,
        head: [['Operator', 'Total Wells', 'Active Wells', 'Oil (BBL)', 'Momentum', 'Growth']],
        body: operatorData,
        theme: 'striped',
        headStyles: { fillColor: [234, 179, 8], textColor: [0, 0, 0] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 8 }
      });

      // Top Counties Section
      const topCountiesY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Top Counties by Oil Production', 14, topCountiesY);

      const countyData = counties.slice(0, 10).map(c => [
        c.name,
        formatNumber(c.total_oil),
        formatNumber(c.total_gas),
        c.well_count?.toLocaleString() || '0',
        c.oil_rank ? `#${c.oil_rank}` : '-'
      ]);

      doc.autoTable({
        startY: topCountiesY + 5,
        head: [['County', 'Oil (BBL)', 'Gas (MCF)', 'Wells', 'Rank']],
        body: countyData,
        theme: 'striped',
        headStyles: { fillColor: [239, 68, 68], textColor: [255, 255, 255] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9 }
      });

      // Footer
      const footerY = doc.internal.pageSize.getHeight() - 15;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Data Source: Texas Railroad Commission (RRC) October 2025', 14, footerY);
      doc.text('Forecast Wells | forecastwells.com', pageWidth - 60, footerY);

      doc.save(`forecast_wells_report_${new Date().toISOString().split('T')[0]}.pdf`);
      setExporting(null);
      setExported(prev => [...prev, 'summary-pdf']);
    }, 800);
  };

  const exportOptions = [
    {
      id: 'operators-csv',
      title: 'Operators Data',
      description: 'Export all operator data including wells, production, and momentum scores',
      icon: Building2,
      format: 'CSV',
      count: operators?.length || 0,
      onClick: exportOperatorsCSV,
      color: 'text-primary'
    },
    {
      id: 'counties-csv',
      title: 'Counties Data',
      description: 'Export county-level production data with rankings and coordinates',
      icon: BarChart3,
      format: 'CSV',
      count: counties?.length || 0,
      onClick: exportCountiesCSV,
      color: 'text-secondary'
    },
    {
      id: 'wells-csv',
      title: 'Wells Data',
      description: 'Export well-level data including API numbers, operators, and status',
      icon: Droplets,
      format: 'CSV',
      count: Math.min(wells?.length || 0, 1000),
      onClick: exportWellsCSV,
      color: 'text-success'
    },
    {
      id: 'summary-pdf',
      title: 'Executive Summary Report',
      description: 'Generate a comprehensive PDF report with basin overview and top performers',
      icon: FileText,
      format: 'PDF',
      count: null,
      onClick: exportSummaryPDF,
      color: 'text-danger'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-heading font-bold tracking-wide uppercase">
            Export Data
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Download production data and reports
          </p>
        </div>
        <Badge variant="outline" className="border-primary/50 text-primary font-mono text-xs">
          <Download className="w-3 h-3 mr-1" />
          {exported.length} exported
        </Badge>
      </div>

      {/* Export Options Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {exportOptions.map((option) => {
          const Icon = option.icon;
          const isExporting = exporting === option.id;
          const isExported = exported.includes(option.id);
          
          return (
            <Card 
              key={option.id}
              className={`bg-surface border-white/10 transition-all hover:border-white/20 ${
                isExported ? 'border-success/50' : ''
              }`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-white/5 ${option.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <CardTitle className="text-sm">{option.title}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-[10px]">
                          {option.format}
                        </Badge>
                        {option.count !== null && (
                          <span className="text-[10px] text-muted-foreground">
                            {option.count.toLocaleString()} records
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {isExported && (
                    <CheckCircle className="w-5 h-5 text-success" />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-xs mb-4">
                  {option.description}
                </CardDescription>
                <Button
                  onClick={option.onClick}
                  disabled={isExporting}
                  className="w-full"
                  variant={isExported ? 'outline' : 'default'}
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : isExported ? (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download Again
                    </>
                  ) : (
                    <>
                      {option.format === 'CSV' ? (
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                      ) : (
                        <FileText className="w-4 h-4 mr-2" />
                      )}
                      Export {option.format}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Data Source Info */}
      <Card className="bg-surface/50 border-white/5">
        <CardContent className="py-4">
          <p className="text-xs text-muted-foreground text-center">
            Data Source: Texas Railroad Commission (RRC) October 2025 Statistics
            <br />
            All exports include the most recent available production data.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExportPage;
